import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import kitApi, {
    type BackfillKitConvoTranscriptRes,
    type GetNetworkKitThankYouEmailPreviewRes,
    type KitConvoListItem,
    type KitConvoStatusDto,
} from '@/api/kit/KitApi'
import ProfilePic from '@/uiV2/common/person/ProfilePic'
import { formatPersonName } from '@/utils/personUtils'
import { formatDateAllInfo } from '@/utils/dateUtils'
import Button from '@/components/Button'
import Modal from '@/components/Modal'
import Note from '@/components/Note'
import { NoteType } from '@/enums/NoteType'
import KitPaginationControls from './KitPaginationControls'
import { Pages } from '@/Pages'

const PAGE_SIZE = 50
const CONVO_TABLE_CLASS =
    'main-table w-full min-w-[1100px] text-[11px] leading-tight'
const CONVO_HEADER_CELL_CLASS =
    'px-2 py-1 text-left align-top whitespace-normal'
const CONVO_CELL_CLASS = 'px-2 py-1 align-top'
const TRANSCRIPT_AVAILABLE_STATUS: KitConvoStatusDto = 'Transcript Available'
const NO_TRANSCRIPTION_STATUS: KitConvoStatusDto = 'No Transcription'

const getTranscriptNotReadyMessage = (
    result: BackfillKitConvoTranscriptRes,
): string =>
    result.message ??
    `Transcript is not ready yet in ElevenLabs. Current status: ${
        result.conversationStatus ?? 'unknown'
    }.`

const sanitizeFileSegment = (value: string): string => {
    const sanitized = value.trim().replace(/[^a-zA-Z0-9_-]+/g, '_')
    return sanitized.length > 0 ? sanitized : 'kit_convos'
}

const downloadBlobFile = (blob: Blob, filename: string): void => {
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
}

const renderConvoStatusBadge = (
    status: KitConvoStatusDto,
): React.JSX.Element => {
    if (status === 'Live') {
        return (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">
                <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600" />
                </span>
                Live
            </span>
        )
    }

    if (status === 'Transcript Available') {
        return (
            <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                Transcript Available
            </span>
        )
    }

    if (status === 'No Transcription') {
        return (
            <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                No Transcription
            </span>
        )
    }

    return (
        <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-700">
            Call Ended
        </span>
    )
}

export default function KitConversationsTab(): React.JSX.Element {
    const [convos, setConvos] = useState<KitConvoListItem[]>([])
    const [sourceOptions, setSourceOptions] = useState<string[]>([])
    const [selectedSource, setSelectedSource] = useState('')
    const [totalTranscriptCount, setTotalTranscriptCount] = useState(0)
    const [availableTranscriptCount, setAvailableTranscriptCount] = useState(0)
    const [missingTranscriptCount, setMissingTranscriptCount] = useState(0)
    const [pageNumber, setPageNumber] = useState(1)
    const [hasMore, setHasMore] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [hasRequested, setHasRequested] = useState(false)
    const [backfillingConvoIds, setBackfillingConvoIds] = useState<
        Record<string, boolean>
    >({})
    const [exportingConvoIds, setExportingConvoIds] = useState<
        Record<string, boolean>
    >({})
    const [pendingTranscriptExportConvo, setPendingTranscriptExportConvo] =
        useState<KitConvoListItem | null>(null)
    const [pendingTranscriptExportError, setPendingTranscriptExportError] =
        useState<string | null>(null)
    const [isBulkExporting, setIsBulkExporting] = useState(false)
    const [showBulkExportFetchModal, setShowBulkExportFetchModal] =
        useState(false)
    const [bulkExportError, setBulkExportError] = useState<string | null>(null)
    const [showBulkRescoreModal, setShowBulkRescoreModal] = useState(false)
    const [isBulkRescoring, setIsBulkRescoring] = useState(false)
    const [bulkRescoreError, setBulkRescoreError] = useState<string | null>(
        null,
    )
    const [bulkRescoreResult, setBulkRescoreResult] = useState<{
        total: number
        succeeded: number
        failed: number
    } | null>(null)
    const [networkKitEmailModalConvo, setNetworkKitEmailModalConvo] =
        useState<KitConvoListItem | null>(null)
    const [networkKitEmailPreview, setNetworkKitEmailPreview] =
        useState<GetNetworkKitThankYouEmailPreviewRes | null>(null)
    const [networkKitEmailPreviewError, setNetworkKitEmailPreviewError] =
        useState<string | null>(null)
    const [networkKitEmailSendError, setNetworkKitEmailSendError] = useState<
        string | null
    >(null)
    const [
        isNetworkKitEmailPreviewLoading,
        setIsNetworkKitEmailPreviewLoading,
    ] = useState(false)
    const [isNetworkKitEmailSending, setIsNetworkKitEmailSending] =
        useState(false)
    const networkKitEmailPreviewRequestIdRef = useRef(0)

    const fetchConvos = useCallback(async (page: number, source: string) => {
        try {
            setIsLoading(true)
            setError(null)
            const normalizedSource = source.trim()
            const res = await kitApi.getConvosAdmin({
                pageNumber: page,
                perPage: PAGE_SIZE,
                source: normalizedSource || undefined,
            })
            setConvos(res.convos)
            setSourceOptions(res.sources)
            setTotalTranscriptCount(res.totalTranscriptCount)
            setAvailableTranscriptCount(res.availableTranscriptCount)
            setMissingTranscriptCount(res.missingTranscriptCount)
            setHasMore(res.hasMore)
        } catch (err) {
            console.error('Failed to load Kit conversations', err)
            setError('Unable to load Kit conversations.')
            setTotalTranscriptCount(0)
            setAvailableTranscriptCount(0)
            setMissingTranscriptCount(0)
        } finally {
            setIsLoading(false)
            setHasRequested(true)
        }
    }, [])

    useEffect(() => {
        void fetchConvos(pageNumber, selectedSource)
    }, [fetchConvos, pageNumber, selectedSource])

    const backfillConvoTranscript = useCallback(
        async (
            convo: KitConvoListItem,
        ): Promise<BackfillKitConvoTranscriptRes | null> => {
            const conversationId = convo.convId.trim()
            if (!conversationId) {
                setError(
                    'Unable to backfill transcript: missing conversation ID.',
                )
                return null
            }

            setError(null)
            setBackfillingConvoIds((current) => ({
                ...current,
                [convo.id]: true,
            }))

            try {
                const result = await kitApi.backfillConvoTranscript({
                    conversationId,
                })

                if (result.backfillStatus === 'not_ready') {
                    setError(getTranscriptNotReadyMessage(result))
                    return result
                }

                await fetchConvos(pageNumber, selectedSource)
                return result
            } catch (err) {
                console.error(
                    `Failed to backfill transcript for Kit conversation ${convo.id}`,
                    err,
                )
                setError(`Unable to backfill transcript for ${conversationId}.`)
                return null
            } finally {
                setBackfillingConvoIds((current) => {
                    const next = { ...current }
                    delete next[convo.id]
                    return next
                })
            }
        },
        [fetchConvos, pageNumber, selectedSource],
    )

    const downloadConvoCsv = useCallback(async (convo: KitConvoListItem) => {
        const conversationId = convo.convId.trim()
        if (!conversationId) {
            setError('Unable to export CSV: missing conversation ID.')
            return
        }

        setError(null)
        setExportingConvoIds((current) => ({
            ...current,
            [convo.id]: true,
        }))

        try {
            const blob = await kitApi.exportConvoCsv(conversationId)
            downloadBlobFile(blob, `kit_convo_${convo.id}.csv`)
        } catch (err) {
            console.error(
                `Failed to export CSV for Kit conversation ${convo.id}`,
                err,
            )
            setError(`Unable to export CSV for ${conversationId}.`)
        } finally {
            setExportingConvoIds((current) => {
                const next = { ...current }
                delete next[convo.id]
                return next
            })
        }
    }, [])

    const closePendingTranscriptExportModal = useCallback(() => {
        setPendingTranscriptExportConvo(null)
        setPendingTranscriptExportError(null)
    }, [])

    const exportConvoCsv = useCallback(
        async (convo: KitConvoListItem) => {
            const canExportDirectly =
                convo.status === TRANSCRIPT_AVAILABLE_STATUS ||
                convo.status === NO_TRANSCRIPTION_STATUS

            if (!canExportDirectly) {
                setPendingTranscriptExportError(null)
                setPendingTranscriptExportConvo(convo)
                return
            }

            await downloadConvoCsv(convo)
        },
        [downloadConvoCsv],
    )

    const fetchTranscriptBeforeExport = useCallback(async () => {
        if (!pendingTranscriptExportConvo) {
            return
        }

        setPendingTranscriptExportError(null)

        const result = await backfillConvoTranscript(
            pendingTranscriptExportConvo,
        )
        if (!result) {
            setPendingTranscriptExportError(
                'Unable to fetch transcript right now. Please try again.',
            )
            return
        }

        if (result.backfillStatus === 'not_ready') {
            setPendingTranscriptExportError(
                getTranscriptNotReadyMessage(result),
            )
            return
        }

        const convoToExport = pendingTranscriptExportConvo
        closePendingTranscriptExportModal()
        await downloadConvoCsv(convoToExport)
    }, [
        backfillConvoTranscript,
        closePendingTranscriptExportModal,
        downloadConvoCsv,
        pendingTranscriptExportConvo,
    ])

    const closeBulkExportFetchModal = useCallback(() => {
        if (isBulkExporting) {
            return
        }

        setShowBulkExportFetchModal(false)
        setBulkExportError(null)
    }, [isBulkExporting])

    const closeBulkRescoreModal = useCallback(() => {
        if (isBulkRescoring) {
            return
        }

        setShowBulkRescoreModal(false)
        setBulkRescoreError(null)
        setBulkRescoreResult(null)
    }, [isBulkRescoring])

    const handleBulkRescoreClick = useCallback(() => {
        if (
            !selectedSource.trim() ||
            totalTranscriptCount <= 0 ||
            isBulkRescoring
        ) {
            return
        }

        setBulkRescoreError(null)
        setBulkRescoreResult(null)
        setShowBulkRescoreModal(true)
    }, [isBulkRescoring, selectedSource, totalTranscriptCount])

    const executeBulkRescore = useCallback(async () => {
        setIsBulkRescoring(true)
        setBulkRescoreError(null)
        setBulkRescoreResult(null)

        try {
            const result = await kitApi.bulkRetriggerScoring({
                source: selectedSource.trim(),
            })
            setBulkRescoreResult({
                total: result.total,
                succeeded: result.succeeded,
                failed: result.failed,
            })
            await fetchConvos(pageNumber, selectedSource)
        } catch (err) {
            console.error('Failed to bulk rescore conversations', err)
            setBulkRescoreError(
                'Unable to rescore conversations. Please try again.',
            )
        } finally {
            setIsBulkRescoring(false)
        }
    }, [fetchConvos, pageNumber, selectedSource])

    const closeNetworkKitEmailModal = useCallback(() => {
        networkKitEmailPreviewRequestIdRef.current += 1
        setNetworkKitEmailModalConvo(null)
        setNetworkKitEmailPreview(null)
        setNetworkKitEmailPreviewError(null)
        setNetworkKitEmailSendError(null)
        setIsNetworkKitEmailPreviewLoading(false)
        setIsNetworkKitEmailSending(false)
    }, [])

    const openNetworkKitEmailModal = useCallback(
        async (convo: KitConvoListItem) => {
            const normalizedConvId = convo.convId.trim()
            if (!normalizedConvId) {
                setError(
                    'Unable to send Network Kit email: missing conversation ID.',
                )
                return
            }

            setNetworkKitEmailModalConvo(convo)
            setNetworkKitEmailPreview(null)
            setNetworkKitEmailPreviewError(null)
            setNetworkKitEmailSendError(null)
            setIsNetworkKitEmailSending(false)

            const requestId = networkKitEmailPreviewRequestIdRef.current + 1
            networkKitEmailPreviewRequestIdRef.current = requestId
            setIsNetworkKitEmailPreviewLoading(true)

            try {
                const preview =
                    await kitApi.getNetworkKitThankYouEmailPreview(
                        normalizedConvId,
                    )
                if (networkKitEmailPreviewRequestIdRef.current !== requestId) {
                    return
                }

                setNetworkKitEmailPreview(preview)
            } catch (err) {
                if (networkKitEmailPreviewRequestIdRef.current !== requestId) {
                    return
                }

                console.error(
                    `Failed to load Network Kit email preview for Kit conversation ${convo.id}`,
                    err,
                )
                setNetworkKitEmailPreviewError(
                    'Unable to load previous Network Kit thank you emails for this user.',
                )
            } finally {
                if (networkKitEmailPreviewRequestIdRef.current === requestId) {
                    setIsNetworkKitEmailPreviewLoading(false)
                }
            }
        },
        [],
    )

    const sendNetworkKitEmail = useCallback(async () => {
        if (!networkKitEmailModalConvo || isNetworkKitEmailSending) {
            return
        }

        const normalizedConvId = networkKitEmailModalConvo.convId.trim()
        if (!normalizedConvId) {
            setNetworkKitEmailSendError(
                'Unable to send Network Kit email: missing conversation ID.',
            )
            return
        }

        setNetworkKitEmailSendError(null)
        setIsNetworkKitEmailSending(true)

        try {
            const result = await kitApi.sendNetworkKitThankYouEmail({
                convId: normalizedConvId,
            })

            if (!result.sent) {
                setNetworkKitEmailSendError(
                    `Unable to send Network Kit email to ${
                        result.recipientEmail ?? 'this user'
                    }.`,
                )
                return
            }

            closeNetworkKitEmailModal()
            await fetchConvos(pageNumber, selectedSource)
        } catch (err) {
            console.error(
                `Failed to send Network Kit email for Kit conversation ${networkKitEmailModalConvo.id}`,
                err,
            )
            setNetworkKitEmailSendError('Unable to send Network Kit email.')
        } finally {
            setIsNetworkKitEmailSending(false)
        }
    }, [
        closeNetworkKitEmailModal,
        fetchConvos,
        isNetworkKitEmailSending,
        networkKitEmailModalConvo,
        pageNumber,
        selectedSource,
    ])

    const exportAllConvoTranscripts = useCallback(
        async (fetchMissing: boolean) => {
            if (totalTranscriptCount <= 0) {
                return
            }

            setError(null)
            setBulkExportError(null)
            setIsBulkExporting(true)

            try {
                const blob = await kitApi.exportAllConvoTranscriptsCsv({
                    source: selectedSource.trim() || undefined,
                    fetchMissing,
                })

                const sourceSegment = selectedSource.trim()
                    ? sanitizeFileSegment(selectedSource)
                    : 'all_sources'
                downloadBlobFile(
                    blob,
                    `kit_convos_${sourceSegment}_${totalTranscriptCount}.csv`,
                )

                setShowBulkExportFetchModal(false)
                await fetchConvos(pageNumber, selectedSource)
            } catch (err) {
                console.error('Failed to export all Kit transcripts', err)
                const message = fetchMissing
                    ? 'Unable to fetch missing transcripts and export all transcripts.'
                    : 'Unable to export all transcripts.'
                setBulkExportError(message)
                setError(message)
            } finally {
                setIsBulkExporting(false)
            }
        },
        [fetchConvos, pageNumber, selectedSource, totalTranscriptCount],
    )

    const handleExportAllTranscriptsClick = useCallback(() => {
        if (isBulkExporting || totalTranscriptCount <= 0) {
            return
        }

        setBulkExportError(null)

        if (missingTranscriptCount > 0) {
            setShowBulkExportFetchModal(true)
            return
        }

        void exportAllConvoTranscripts(false)
    }, [
        exportAllConvoTranscripts,
        isBulkExporting,
        missingTranscriptCount,
        totalTranscriptCount,
    ])

    const exportAllTranscriptsButtonLabel = useMemo(
        () => `Export All Transcripts (${totalTranscriptCount})`,
        [totalTranscriptCount],
    )

    const [activeDynamicVars, setActiveDynamicVars] = useState<string | null>(
        null,
    )
    const isPendingTranscriptExportBusy = pendingTranscriptExportConvo
        ? Boolean(
              backfillingConvoIds[pendingTranscriptExportConvo.id] ||
              exportingConvoIds[pendingTranscriptExportConvo.id],
          )
        : false

    const tableRows = useMemo(
        () =>
            convos.map((convo) => {
                const person = convo.person
                const personName = person ? formatPersonName(person) : ''
                const elevenLabsUrl = `https://elevenlabs.io/app/agents/history/${convo.convId}`
                const hasDynamicVars = Boolean(convo.dynamicVariables?.trim())
                const jobsOfferedCount =
                    typeof convo.jobsOfferedCount === 'number'
                        ? Math.max(0, convo.jobsOfferedCount)
                        : 0
                const interestedJobsCount =
                    typeof convo.interestedJobsCount === 'number'
                        ? Math.max(0, convo.interestedJobsCount)
                        : 0
                const notInterestedJobsCount =
                    typeof convo.notInterestedJobsCount === 'number'
                        ? Math.max(0, convo.notInterestedJobsCount)
                        : 0
                const erroredToolCallCount =
                    typeof convo.erroredToolCallCount === 'number'
                        ? Math.max(0, convo.erroredToolCallCount)
                        : 0
                const networkKitThankYouEmailCount =
                    typeof convo.networkKitThankYouEmailCount === 'number'
                        ? Math.max(
                              0,
                              Math.trunc(convo.networkKitThankYouEmailCount),
                          )
                        : 0
                const debugConsoleUrl = `${Pages.KitConvoDebug}?kitConvoId=${encodeURIComponent(
                    convo.id,
                )}&convId=${encodeURIComponent(convo.convId)}`
                const isBackfillingTranscript = Boolean(
                    backfillingConvoIds[convo.id],
                )
                const isExportingConvo = Boolean(exportingConvoIds[convo.id])
                const isActiveNetworkKitEmailConvo =
                    networkKitEmailModalConvo?.id === convo.id
                const isNetworkKitEmailBusy =
                    isActiveNetworkKitEmailConvo &&
                    (isNetworkKitEmailPreviewLoading ||
                        isNetworkKitEmailSending)

                return (
                    <tr key={convo.id}>
                        <td className={CONVO_CELL_CLASS}>{convo.id}</td>
                        <td className={CONVO_CELL_CLASS}>
                            <a
                                className="text-blue-600 hover:underline text-xs font-semibold"
                                href={elevenLabsUrl}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {convo.convId}
                            </a>
                        </td>
                        <td className={CONVO_CELL_CLASS}>
                            <div className="flex flex-col gap-1">
                                <span>{convo.user.email}</span>
                                <span className="text-[10px] text-gray-500">
                                    Name: {convo.user.name?.trim() || '—'}
                                </span>
                                <span className="text-[10px] text-gray-500">
                                    User ID: {convo.user.id}
                                </span>
                            </div>
                        </td>
                        <td className={CONVO_CELL_CLASS}>
                            {person ? (
                                <div className="flex items-center gap-3">
                                    <ProfilePic
                                        src={
                                            person.linkedInProfilePicUrl ??
                                            undefined
                                        }
                                        size={28}
                                    />
                                    <div className="flex flex-col">
                                        <span className="text-[11px] font-semibold leading-tight">
                                            {personName || 'Unknown person'}
                                        </span>
                                        <span className="text-[10px] text-gray-500">
                                            {person.id}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <span className="text-gray-500 text-xs">
                                    Not linked
                                </span>
                            )}
                        </td>
                        <td className={CONVO_CELL_CLASS}>
                            {formatDateAllInfo(convo.createdAt)}
                        </td>
                        <td className={CONVO_CELL_CLASS}>
                            <div className="flex flex-col items-start gap-1">
                                {renderConvoStatusBadge(convo.status)}
                                {(convo.status === 'Call Ended' ||
                                    convo.status === 'Live') && (
                                    <button
                                        type="button"
                                        className={`text-[10px] font-semibold ${
                                            isBackfillingTranscript
                                                ? 'text-gray-400 cursor-not-allowed'
                                                : 'text-blue-600 hover:underline'
                                        }`}
                                        onClick={() =>
                                            void backfillConvoTranscript(convo)
                                        }
                                        disabled={isBackfillingTranscript}
                                    >
                                        {isBackfillingTranscript
                                            ? 'Fetching transcript...'
                                            : 'Fetch transcript'}
                                    </button>
                                )}
                            </div>
                        </td>
                        <td className={CONVO_CELL_CLASS}>
                            {convo.source ? (
                                <span className="text-xs text-gray-700">
                                    {convo.source}
                                </span>
                            ) : (
                                <span className="text-gray-500 text-xs">—</span>
                            )}
                        </td>
                        <td className={CONVO_CELL_CLASS}>
                            <div className="flex flex-col items-start gap-1">
                                <span
                                    className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                                        networkKitThankYouEmailCount > 0
                                            ? 'border-emerald-200 bg-emerald-100 text-emerald-700'
                                            : 'border-gray-200 bg-gray-100 text-gray-700'
                                    }`}
                                >
                                    Network Kit: {networkKitThankYouEmailCount}
                                </span>
                                <Button
                                    type="button"
                                    style="secondary"
                                    size="small"
                                    onClick={() =>
                                        void openNetworkKitEmailModal(convo)
                                    }
                                    enabled={!isNetworkKitEmailBusy}
                                >
                                    {isNetworkKitEmailBusy
                                        ? 'Loading...'
                                        : 'Send Network Kit Email'}
                                </Button>
                            </div>
                        </td>
                        <td className={CONVO_CELL_CLASS}>
                            {hasDynamicVars ? (
                                <button
                                    type="button"
                                    className="text-blue-600 hover:underline text-xs font-semibold"
                                    onClick={() =>
                                        setActiveDynamicVars(
                                            convo.dynamicVariables ?? '',
                                        )
                                    }
                                >
                                    View dynamic variables
                                </button>
                            ) : (
                                <span className="text-gray-500 text-xs">—</span>
                            )}
                        </td>
                        <td className={CONVO_CELL_CLASS}>
                            <div className="flex flex-col items-start gap-1">
                                <Link
                                    className="text-blue-600 hover:underline text-xs font-semibold"
                                    to={debugConsoleUrl}
                                >
                                    Debug Console
                                </Link>
                                <button
                                    type="button"
                                    className={`text-[10px] font-semibold ${
                                        isExportingConvo
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : 'text-blue-600 hover:underline'
                                    }`}
                                    onClick={() => void exportConvoCsv(convo)}
                                    disabled={isExportingConvo}
                                >
                                    {isExportingConvo
                                        ? 'Exporting CSV...'
                                        : 'Export CSV'}
                                </button>
                                {convo.hasToolCalls && (
                                    <>
                                        <span className="rounded-full border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-emerald-700">
                                            Tools Called
                                        </span>
                                        <span
                                            className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                                                jobsOfferedCount > 0
                                                    ? 'border-emerald-200 bg-emerald-100 text-emerald-700'
                                                    : 'border-gray-200 bg-gray-100 text-gray-700'
                                            }`}
                                        >
                                            {jobsOfferedCount} Jobs Offered
                                        </span>
                                        {jobsOfferedCount > 0 &&
                                            interestedJobsCount > 0 && (
                                                <span className="rounded-full border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                                    Interested in{' '}
                                                    {interestedJobsCount} Jobs
                                                </span>
                                            )}
                                        {jobsOfferedCount > 0 &&
                                            notInterestedJobsCount > 0 && (
                                                <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                                                    Not Interested in{' '}
                                                    {notInterestedJobsCount}{' '}
                                                    Jobs
                                                </span>
                                            )}
                                        {erroredToolCallCount > 0 && (
                                            <span className="rounded-full border border-red-200 bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">
                                                {erroredToolCallCount}{' '}
                                                {erroredToolCallCount === 1
                                                    ? 'Error'
                                                    : 'Errors'}
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>
                        </td>
                    </tr>
                )
            }),
        [
            backfillingConvoIds,
            backfillConvoTranscript,
            convos,
            exportConvoCsv,
            exportingConvoIds,
            isNetworkKitEmailPreviewLoading,
            isNetworkKitEmailSending,
            networkKitEmailModalConvo,
            openNetworkKitEmailModal,
        ],
    )

    const networkKitEmailRecipientEmail =
        networkKitEmailPreview?.userEmail ??
        networkKitEmailModalConvo?.user.email
    const currentConvoNetworkKitThankYouCount =
        networkKitEmailPreview?.currentConvoThankYouEmailCount ?? 0
    const otherNetworkKitThankYouConvos =
        networkKitEmailPreview?.otherConvosWithThankYou ?? []
    const canSendNetworkKitEmail =
        !isNetworkKitEmailPreviewLoading &&
        !isNetworkKitEmailSending &&
        networkKitEmailPreview !== null &&
        !networkKitEmailPreviewError
    const showCurrentConvoAlreadySentWarning =
        currentConvoNetworkKitThankYouCount > 0
    const showOtherConvosAlreadySentWarning =
        otherNetworkKitThankYouConvos.length > 0

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="page-title">Conversations</h3>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                        <label
                            htmlFor="kit-convo-source-filter"
                            className="text-xs font-semibold uppercase tracking-wide text-gray-600"
                        >
                            Source
                        </label>
                        <select
                            id="kit-convo-source-filter"
                            className="form-input h-[36px] min-w-[220px] py-1 text-sm"
                            value={selectedSource}
                            onChange={(event) => {
                                setSelectedSource(event.target.value)
                                setPageNumber(1)
                                setShowBulkExportFetchModal(false)
                                setBulkExportError(null)
                                setShowBulkRescoreModal(false)
                                setBulkRescoreError(null)
                                setBulkRescoreResult(null)
                            }}
                            disabled={isLoading}
                        >
                            <option value="">All sources</option>
                            {sourceOptions.map((source) => (
                                <option key={source} value={source}>
                                    {source}
                                </option>
                            ))}
                        </select>
                    </div>
                    <Button
                        type="button"
                        style="secondary"
                        onClick={() =>
                            void fetchConvos(pageNumber, selectedSource)
                        }
                        enabled={!isLoading}
                    >
                        Refresh
                    </Button>
                    <Button
                        type="button"
                        style="primary"
                        onClick={() => void handleExportAllTranscriptsClick()}
                        enabled={
                            !isLoading &&
                            !isBulkExporting &&
                            totalTranscriptCount > 0
                        }
                    >
                        {isBulkExporting
                            ? 'Exporting transcripts...'
                            : exportAllTranscriptsButtonLabel}
                    </Button>
                    <span className="text-xs text-gray-500">
                        {availableTranscriptCount} ready /{' '}
                        {missingTranscriptCount} not fetched
                    </span>
                    {selectedSource.trim() && (
                        <Button
                            type="button"
                            style="danger"
                            onClick={() => void handleBulkRescoreClick()}
                            enabled={
                                !isLoading &&
                                !isBulkRescoring &&
                                totalTranscriptCount > 0
                            }
                        >
                            {isBulkRescoring
                                ? 'Rescoring...'
                                : `Rescore All Rubrics (${totalTranscriptCount})`}
                        </Button>
                    )}
                </div>
            </div>
            {error && (
                <div className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {error}
                </div>
            )}
            {isLoading && !hasRequested ? (
                <p className="mt-4 text-sm text-gray-500">
                    Loading conversations...
                </p>
            ) : convos.length === 0 ? (
                <p className="mt-4 text-sm text-gray-500">
                    {hasRequested
                        ? 'No Kit conversations found.'
                        : 'Select this tab to load conversations.'}
                </p>
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className={CONVO_TABLE_CLASS}>
                            <thead>
                                <tr className="text-[10px] uppercase tracking-wide">
                                    <th className={CONVO_HEADER_CELL_CLASS}>
                                        KitConvo Id
                                    </th>
                                    <th className={CONVO_HEADER_CELL_CLASS}>
                                        ElevenLabs Conv Id
                                    </th>
                                    <th className={CONVO_HEADER_CELL_CLASS}>
                                        User
                                    </th>
                                    <th className={CONVO_HEADER_CELL_CLASS}>
                                        Person
                                    </th>
                                    <th className={CONVO_HEADER_CELL_CLASS}>
                                        Created At
                                    </th>
                                    <th className={CONVO_HEADER_CELL_CLASS}>
                                        Status
                                    </th>
                                    <th className={CONVO_HEADER_CELL_CLASS}>
                                        Source
                                    </th>
                                    <th className={CONVO_HEADER_CELL_CLASS}>
                                        Thank You Email Sent
                                    </th>
                                    <th className={CONVO_HEADER_CELL_CLASS}>
                                        Dynamic Variables
                                    </th>
                                    <th className={CONVO_HEADER_CELL_CLASS}>
                                        Debug Console
                                    </th>
                                </tr>
                            </thead>
                            <tbody>{tableRows}</tbody>
                        </table>
                    </div>
                    <KitPaginationControls
                        pageNumber={pageNumber}
                        itemCount={convos.length}
                        perPage={PAGE_SIZE}
                        hasMore={hasMore}
                        isLoading={isLoading}
                        onPrevious={() =>
                            setPageNumber((page) => Math.max(1, page - 1))
                        }
                        onNext={() => setPageNumber((page) => page + 1)}
                    />
                </>
            )}
            {activeDynamicVars !== null && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="w-full max-w-2xl rounded-xl bg-white p-5 shadow-xl">
                        <div className="flex items-center justify-between gap-4">
                            <h4 className="text-sm font-semibold text-gray-900">
                                Dynamic variables
                            </h4>
                            <button
                                type="button"
                                className="text-xs font-semibold text-gray-500 hover:text-gray-700"
                                onClick={() => setActiveDynamicVars(null)}
                            >
                                Close
                            </button>
                        </div>
                        <div className="mt-3 max-h-[60vh] overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-800">
                            <pre className="whitespace-pre-wrap">
                                {activeDynamicVars}
                            </pre>
                        </div>
                    </div>
                </div>
            )}
            {networkKitEmailModalConvo !== null && (
                <Modal
                    title="Send Network Kit Email"
                    onClose={closeNetworkKitEmailModal}
                    allowUserToClose={!isNetworkKitEmailSending}
                    rightFooter={
                        <>
                            <Button
                                type="button"
                                style="secondary"
                                onClick={closeNetworkKitEmailModal}
                                enabled={!isNetworkKitEmailSending}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                style="primary"
                                onClick={() => void sendNetworkKitEmail()}
                                enabled={canSendNetworkKitEmail}
                            >
                                {isNetworkKitEmailSending
                                    ? 'Sending Email...'
                                    : 'Send Email'}
                            </Button>
                        </>
                    }
                >
                    {isNetworkKitEmailPreviewLoading ? (
                        <p className="text-sm text-gray-700">
                            Loading previous thank you email history...
                        </p>
                    ) : (
                        <>
                            {networkKitEmailPreviewError ? (
                                <Note type={NoteType.ERROR}>
                                    {networkKitEmailPreviewError}
                                </Note>
                            ) : (
                                <>
                                    <p className="text-sm text-gray-700">
                                        Send Network Kit &quot;thank you&quot;
                                        email to{' '}
                                        <span className="font-semibold text-gray-900">
                                            {networkKitEmailRecipientEmail ??
                                                networkKitEmailModalConvo.user
                                                    .email}
                                        </span>
                                        ?
                                    </p>

                                    {showCurrentConvoAlreadySentWarning && (
                                        <div className="mt-3">
                                            <Note type={NoteType.ERROR}>
                                                This user{' '}
                                                {networkKitEmailRecipientEmail ??
                                                    networkKitEmailModalConvo
                                                        .user.email}{' '}
                                                has already recieved an email
                                                for this conversation - are you
                                                sure you want to send again?
                                            </Note>
                                        </div>
                                    )}

                                    {showOtherConvosAlreadySentWarning && (
                                        <div className="mt-3">
                                            <Note type={NoteType.WARNING}>
                                                <div className="space-y-2">
                                                    <p>
                                                        User{' '}
                                                        {networkKitEmailRecipientEmail ??
                                                            networkKitEmailModalConvo
                                                                .user
                                                                .email}{' '}
                                                        has recieved a thank you
                                                        email from the following
                                                        conversations:
                                                    </p>
                                                    <ul className="list-disc space-y-1 pl-5">
                                                        {otherNetworkKitThankYouConvos.map(
                                                            (convo) => {
                                                                const convoDebugUrl = `${Pages.KitConvoDebug}?kitConvoId=${encodeURIComponent(
                                                                    convo.kitConvoId,
                                                                )}&convId=${encodeURIComponent(
                                                                    convo.convId,
                                                                )}`

                                                                return (
                                                                    <li
                                                                        key={
                                                                            convo.kitConvoId
                                                                        }
                                                                    >
                                                                        <Link
                                                                            className="font-semibold text-blue-600 hover:underline"
                                                                            to={
                                                                                convoDebugUrl
                                                                            }
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                        >
                                                                            {
                                                                                convo.kitConvoId
                                                                            }{' '}
                                                                            (
                                                                            {
                                                                                convo.convId
                                                                            }
                                                                            )
                                                                        </Link>{' '}
                                                                        ({' '}
                                                                        {
                                                                            convo.networkKitThankYouEmailCount
                                                                        }{' '}
                                                                        sent)
                                                                    </li>
                                                                )
                                                            },
                                                        )}
                                                    </ul>
                                                </div>
                                            </Note>
                                        </div>
                                    )}
                                </>
                            )}
                            {networkKitEmailSendError && (
                                <div className="mt-3">
                                    <Note type={NoteType.ERROR}>
                                        {networkKitEmailSendError}
                                    </Note>
                                </div>
                            )}
                        </>
                    )}
                </Modal>
            )}
            {showBulkExportFetchModal && (
                <Modal
                    title="Fetch transcripts first?"
                    onClose={closeBulkExportFetchModal}
                    allowUserToClose={!isBulkExporting}
                    rightFooter={
                        <>
                            <Button
                                type="button"
                                style="secondary"
                                onClick={closeBulkExportFetchModal}
                                enabled={!isBulkExporting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                style="primary"
                                onClick={() =>
                                    void exportAllConvoTranscripts(true)
                                }
                                enabled={!isBulkExporting}
                            >
                                {isBulkExporting
                                    ? `Fetching ${missingTranscriptCount} transcripts...`
                                    : `Fetch ${missingTranscriptCount} & Export ${totalTranscriptCount}`}
                            </Button>
                        </>
                    }
                >
                    <p className="text-sm text-gray-700">
                        {missingTranscriptCount} transcripts are not fetched for
                        this filter. Fetch them server-side before exporting all
                        transcripts?
                    </p>
                    {selectedSource.trim() && (
                        <p className="mt-2 text-xs text-gray-500">
                            Source filter:{' '}
                            <span className="font-semibold text-gray-700">
                                {selectedSource}
                            </span>
                        </p>
                    )}
                    {bulkExportError && (
                        <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                            {bulkExportError}
                        </div>
                    )}
                </Modal>
            )}
            {showBulkRescoreModal && (
                <Modal
                    title="Rescore all rubrics?"
                    onClose={closeBulkRescoreModal}
                    allowUserToClose={!isBulkRescoring}
                    rightFooter={
                        <>
                            <Button
                                type="button"
                                style="secondary"
                                onClick={closeBulkRescoreModal}
                                enabled={!isBulkRescoring}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                style="danger"
                                onClick={() => void executeBulkRescore()}
                                enabled={!isBulkRescoring}
                            >
                                {isBulkRescoring
                                    ? `Rescoring ${totalTranscriptCount} conversations...`
                                    : `Rescore ${totalTranscriptCount} conversations`}
                            </Button>
                        </>
                    }
                >
                    <p className="text-sm text-gray-700">
                        This will{' '}
                        <strong>delete all existing rubric scores</strong> and
                        re-run LLM scoring for{' '}
                        <strong>{totalTranscriptCount}</strong> conversations.
                        This action cannot be undone.
                    </p>
                    <p className="mt-2 text-sm text-gray-700">
                        Each conversation will be scored individually using the
                        current rubric and scoring prompt. This may take several
                        minutes and will incur OpenAI API costs.
                    </p>
                    {selectedSource.trim() && (
                        <p className="mt-2 text-xs text-gray-500">
                            Source filter:{' '}
                            <span className="font-semibold text-gray-700">
                                {selectedSource}
                            </span>
                        </p>
                    )}
                    {bulkRescoreResult && (
                        <div className="mt-3 rounded border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                            Completed: {bulkRescoreResult.succeeded} succeeded,{' '}
                            {bulkRescoreResult.failed} failed out of{' '}
                            {bulkRescoreResult.total} total.
                        </div>
                    )}
                    {bulkRescoreError && (
                        <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                            {bulkRescoreError}
                        </div>
                    )}
                </Modal>
            )}
            {pendingTranscriptExportConvo !== null && (
                <Modal
                    title="Transcript not available"
                    onClose={closePendingTranscriptExportModal}
                    allowUserToClose={!isPendingTranscriptExportBusy}
                    rightFooter={
                        <>
                            <Button
                                type="button"
                                style="secondary"
                                onClick={closePendingTranscriptExportModal}
                                enabled={!isPendingTranscriptExportBusy}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                style="primary"
                                onClick={() =>
                                    void fetchTranscriptBeforeExport()
                                }
                                enabled={!isPendingTranscriptExportBusy}
                            >
                                {isPendingTranscriptExportBusy
                                    ? 'Fetching transcript...'
                                    : 'Fetch transcript & export'}
                            </Button>
                        </>
                    }
                >
                    <p className="text-sm text-gray-700">
                        This conversation does not have a transcript yet. Fetch
                        the transcript first and then export the CSV?
                    </p>
                    <p className="mt-2 text-xs text-gray-500">
                        Conversation ID:{' '}
                        <span className="font-semibold text-gray-700">
                            {pendingTranscriptExportConvo.convId}
                        </span>
                    </p>
                    {pendingTranscriptExportError && (
                        <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                            {pendingTranscriptExportError}
                        </div>
                    )}
                </Modal>
            )}
        </div>
    )
}
